import { useMemo } from "react"
import { useDimensions } from "./../dimensions"
import { useProfile } from "src/shared/hooks/useProfile"
import { useFormatDate } from "src/shared/hooks/useFormatDate"
import {
    Medidas,
    days,
    getCapitalizeText,
    getCardsPorPagina,
    getEscalaCards,
    getItems,
    getLabelCamas,
    getOrderUrls,
} from "./utils"
import { capitalizeString } from "src/shared/hooks/capitalizeString"
import { DiasDisponibles, TipoTarifa } from "src/gql/schema"
import {
    DisponibilidadHabitacion,
    DisponibilidadTarifa,
    useDisponibilidad,
} from "../../providers/DisponibilidadProvider"

type Tipo = DisponibilidadHabitacion & {
    tarifa: DisponibilidadTarifa
    priceBooking: number
}

// Mayor costo entre las tarifas de reserva visibles en pantalla para el día: prioridad a `reserva`, si no hay se usa `renta_reserva`
const getPrecioReserva = (tarifas: DisponibilidadTarifa[], tipo_habitacion_id: string, day: DiasDisponibles): number => {
    const delTipo = tarifas.filter((t) => t?.tipo_habitacion_id === tipo_habitacion_id && t?.dias_disponibles?.includes(day))
    const reserva = delTipo.filter((t) => t?.tipo_tarifa === TipoTarifa.Reserva)
    const candidatas = reserva.length > 0 ? reserva : delTipo.filter((t) => t?.tipo_tarifa === TipoTarifa.RentaReserva)
    return candidatas.length > 0 ? Math.max(...candidatas.map((t) => Number(t?.costo_habitacion || 0))) : 0
}

// `area` es el tamaño medido del contenedor de las tarjetas (ver `useCardArea`).
export function useData(area: Medidas) {
    const { height, width } = useDimensions()
    const { logo_hotel, configurations } = useProfile()
    const { nowHotel } = useFormatDate()
    const { loading, habitaciones, tarifas, listaEspera, folioTurno, folioAnuncio } = useDisponibilidad()
    const day = days[nowHotel.getDay()]

    // En este backend cada tipo de habitación tiene una sola tarifa de pantalla vigente por día.
    const itemList = useMemo(() => {
        const items: Tipo[] = []

        const array = [...new Map(habitaciones.map((obj) => [obj.tipo_habitacion_id, obj])).values()]
        const tarifas_list = tarifas.filter((t) => t?.pantalla_disponibilidad)

        array.forEach((item) => {
            // Tarifa de renta del tipo para el día: prioridad a `renta`, si no hay se usa `renta_reserva`
            const delTipo = (t: (typeof tarifas_list)[number]) =>
                t?.tipo_habitacion_id === item?.tipo_habitacion_id && t?.dias_disponibles?.includes(day)
            const tarifa =
                tarifas_list.find((t) => delTipo(t) && t?.tipo_tarifa === TipoTarifa.Renta) ||
                tarifas_list.find((t) => delTipo(t) && t?.tipo_tarifa === TipoTarifa.RentaReserva)
            if (tarifa) {
                items.push({
                    ...item,
                    tarifa,
                    priceBooking: getPrecioReserva(tarifas_list, item?.tipo_habitacion_id, day),
                })
            }
        })
        return items
    }, [habitaciones, tarifas, day])

    const viewport = useMemo(() => ({ width, height }), [width, height])
    const escalaCards = getEscalaCards(area, viewport)

    const turnosListaEspera = useMemo(
        () => getItems(listaEspera, getCardsPorPagina(area, viewport, true)),
        [listaEspera, area, viewport]
    )

    const items = useMemo(
        () => getItems(
            itemList.map((d) => {
                const personas_extra = Number(d.tarifa.personas_extra_max || 0)
                const costo_persona_extra = Number(d.tarifa.costo_persona_extra || 0)
                return {
                    title: getCapitalizeText(d.tarifa.nombre || d.nombre || "Sin nombre"),
                    image: d.foto || "",
                    hours: d.tarifa.duracion_renta || 0,
                    size: d.personas_incluidas || 0,
                    disponibles: d.disponibles || 0,
                    price: d.tarifa.costo_habitacion || 0,
                    priceBooking: d.priceBooking > 0 ? d.priceBooking : null,
                    priceExtra: personas_extra > 0 && costo_persona_extra > 0 ? costo_persona_extra : null,
                    maxExtra: personas_extra,
                    amenidades: d.amenidades?.length > 0 ? capitalizeString(d.amenidades.join(", ")) : "",
                    camas: getLabelCamas(d.camas || []),
                    numeroCamas: Number(d.camas?.length || 0),
                }
            }),
            getCardsPorPagina(area, viewport)
        ),
        [itemList, area, viewport]
    )

    return {
        logo: logo_hotel || "",
        loading,
        items,
        escala_cards: escalaCards,
        img_promo_horizontal: configurations?.panel?.imagen_promo?.horizontal?.length
            ? getOrderUrls(configurations.panel.imagen_promo.horizontal)
            : [],
        img_promo_vertical: configurations?.panel?.imagen_promo?.vertical?.length
            ? getOrderUrls(configurations.panel.imagen_promo.vertical)
            : [],
        lista_espera: turnosListaEspera,
        folio_turn: folioTurno,
        folio_anuncio: folioAnuncio,
        // Este backend no tiene avisos ni pie de página en `Panel`: la UI muestra sus estados vacíos.
        avisos: [] as string[],
        pie_pagina: "",
    }
}
